import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import * as modelViewer from '@google/model-viewer';
import type { ProductFormData } from '@/components/ProductForm/ProductForm';
import type { AnnotationEditorRef } from '@/components/Annotations';
import type { Annotation, ModelViewerSettings } from '@/types/Annotation.model';
import type { LinkShareMode } from '@/types/Product.model';
import type { IProduct } from '@/types/Product.model';
import { restoreDeletedProduct } from '@/Api';
import { clientRoutes } from '@/ApiPaths';
import { glbZoomSrc, productGlbUrl } from '@/utils/productModelUrl';
import { loadProductForEdit } from '@/utils/loadProductForEdit';
import { productNameFromModelFilename } from '@/utils/productNameFromModelFilename';
import { isApiError } from '@/services/httpClient';
import {
  hasPendingProductEditChanges,
  type ProductEditDirtySnapshot,
} from '@/utils/productEditDirty';
import {
  DEFAULT_MODEL_VIEWER_PRESENTATION,
  resolvePresentationSettings,
  type ModelViewerPresentationSettings,
} from '@/constants/modelViewerPresentation';
import { useModelUpload } from '@/hooks/useModelUpload';
import { useModelViewerAnimations } from '@/hooks/useModelViewerAnimations';
import { useProductTagSuggestions } from '@/hooks/useProductTagSuggestions';
import { useProductSubmission, type ProductSubmissionData } from '@/hooks/useProductSubmission';
import { downloadFile, downloadGlb, exportModelAsGlb } from '@/services/Exporters';
import { LoadInformation } from '@/services/Loaders';
import { useStore } from '@/store/Store';

export const PRODUCT_EDITOR_FORM_ID = 'product-editor-form';

export type ProductEditorVariant = 'create' | 'edit';

/** Why the edit workspace is blocked (edit route only). */
export type ProductEditBlockReason = 'inTrash' | 'notFound' | 'error' | null;

export type ProductFormInitial = ProductFormData & {
  linkSharePasswordSet?: boolean;
};

const EMPTY_FORM: ProductFormInitial = {
  productName: '',
  productPage: '',
  articleNumber: '',
  isPublic: false,
  descriptionHtml: '',
  linkShareMode: 'open',
  linkSharePassword: '',
  updateLinkSharePassword: false,
  category: '',
  linkSharePasswordSet: false,
};

function cloneMetadata(metadata: IProduct['metadata']): IProduct['metadata'] {
  if (!metadata) return metadata;
  const rows = metadata.rows
    ? Object.fromEntries(
        Object.entries(metadata.rows).map(([key, row]) => [
          key,
          {
            ...row,
            data: { ...(row?.data || {}) },
          },
        ])
      )
    : {};
  return {
    ...metadata,
    columns: { ...(metadata.columns || {}) },
    rows,
  };
}

function formFromProduct(product: IProduct): ProductFormData {
  return {
    productName: product.productName || '',
    productPage: product.productPage || '',
    articleNumber: product.articleNumber || '',
    isPublic: product.isPublic ?? false,
    descriptionHtml: product.descriptionHtml || '',
    // Deep-clone metadata so live form edits cannot mutate the edit baseline.
    metadata: cloneMetadata(product.metadata),
    category: product.category || '',
    linkShareMode: (product.linkShareMode as LinkShareMode) ?? 'open',
    linkSharePassword: '',
    updateLinkSharePassword: false,
  };
}

/**
 * Shared create/edit product workspace state.
 * Both `/createproduct` and `/editproduct` use this so UI and submit paths stay in sync.
 */
export function useProductEditor(variant: ProductEditorVariant) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSessionContext();
  const color = useStore((state) => state.userProfile.qrCodeColor);

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id') ?? '';

  const modelViewerRef = useRef<modelViewer.ModelViewerElement>(null);
  const annotationEditorRef = useRef<AnnotationEditorRef>(null);
  const loadedModelViewerSettingsRef = useRef<Partial<ModelViewerSettings>>({});

  const {
    modelFile,
    modelUrl,
    nonGltfModel,
    preparedGlb,
    setModelFile,
    setModelUrl,
    setNonGltfModel,
    applyPreparedModel,
  } = useModelUpload();
  const availableAnimations = useModelViewerAnimations(modelViewerRef, modelUrl);
  const { suggestedTags, previousTags } = useProductTagSuggestions();

  const [modelPreviewUri, setModelPreviewUri] = useState('');
  const [initialCameraOrbit, setInitialCameraOrbit] = useState('');
  const [initialCameraTarget, setInitialCameraTarget] = useState('');
  const [initialCameraFieldOfView, setInitialCameraFieldOfView] = useState('');
  const [showModelUploader, setShowModelUploader] = useState(false);
  const [modelReplacement, setModelReplacement] = useState(false);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isAnnotationMode, setIsAnnotationMode] = useState(false);
  const [presentationSettings, setPresentationSettings] = useState<ModelViewerPresentationSettings>(
    DEFAULT_MODEL_VIEWER_PRESENTATION
  );

  const [productData, setProductData] = useState<ProductFormInitial>(EMPTY_FORM);
  const [canEditProduct, setCanEditProduct] = useState(Boolean(!id));
  const [editBlockReason, setEditBlockReason] = useState<ProductEditBlockReason>(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(Boolean(id));
  const [isRestoringFromTrash, setIsRestoringFromTrash] = useState(false);
  const [liveFormValues, setLiveFormValues] = useState<ProductFormData | null>(null);
  const [editBaseline, setEditBaseline] = useState<ProductEditDirtySnapshot | null>(null);
  const [modelUploadProductName, setModelUploadProductName] = useState<string | undefined>();

  // Edit route without id → create page
  useEffect(() => {
    if (variant === 'edit' && !id) {
      navigate(clientRoutes.createproduct, { replace: true });
    }
  }, [variant, id, navigate]);

  useEffect(() => {
    if (!modelFile) {
      setModelUploadProductName(undefined);
      return;
    }
    const derived = productNameFromModelFilename(modelFile.name);
    setModelUploadProductName(derived || undefined);
  }, [modelFile]);

  const handleFormValuesChange = useCallback((values: ProductFormData) => {
    setLiveFormValues(values);
  }, []);

  const applyLoadedProduct = useCallback(
    (product: IProduct, productId: string) => {
      // Separate clones so live edits never mutate the dirty-check baseline.
      const loadedForm = formFromProduct(product);
      const baselineForm = formFromProduct(product);
      setProductData({
        ...loadedForm,
        linkSharePasswordSet: Boolean(product.linkSharePasswordSet),
      });
      setLiveFormValues(loadedForm);
      setModelUrl(glbZoomSrc(product, productId) ?? '');

      let loadedOrbit = '';
      let loadedTarget = '';
      let loadedFov = '';
      let loadedAnnotations: Annotation[] = [];
      let loadedPresentation = DEFAULT_MODEL_VIEWER_PRESENTATION;

      if (!product.modelViewerSettings) {
        loadedModelViewerSettingsRef.current = {};
      } else {
        try {
          const settings = JSON.parse(product.modelViewerSettings) as ModelViewerSettings;
          loadedModelViewerSettingsRef.current = { ...settings };
          loadedOrbit = settings.initialCameraOrbit ?? '';
          loadedTarget = settings.initialCameraTarget ?? '';
          loadedFov = settings.initialCameraFieldOfView ?? '';
          loadedAnnotations = settings.annotations ?? [];
          loadedPresentation = resolvePresentationSettings(settings);
          if (loadedOrbit) setInitialCameraOrbit(loadedOrbit);
          if (loadedTarget) setInitialCameraTarget(loadedTarget);
          if (loadedFov) setInitialCameraFieldOfView(loadedFov);
          if (loadedAnnotations.length > 0) setAnnotations(loadedAnnotations);
          setPresentationSettings(loadedPresentation);
        } catch (error) {
          console.error('Error parsing modelViewerSettings:', error);
          loadedModelViewerSettingsRef.current = {};
        }
      }

      setEditBaseline({
        form: baselineForm,
        linkSharePasswordSet: Boolean(product.linkSharePasswordSet),
        previewUri: '',
        cameraOrbit: loadedOrbit,
        cameraTarget: loadedTarget,
        cameraFov: loadedFov,
        annotations: loadedAnnotations,
        presentation: loadedPresentation,
        hasNewModelFile: false,
      });
    },
    [setModelUrl]
  );

  useEffect(() => {
    if (!id) {
      setCanEditProduct(true);
      setEditBlockReason(null);
      setIsLoadingEdit(false);
      return;
    }

    let cancelled = false;
    setCanEditProduct(false);
    setEditBlockReason(null);
    setIsLoadingEdit(true);

    loadProductForEdit(id, navigate, t)
      .then((outcome) => {
        if (cancelled) return;
        if (outcome.status === 'ok') {
          setCanEditProduct(true);
          setEditBlockReason(null);
          applyLoadedProduct(outcome.product, id);
          return;
        }
        if (outcome.status === 'inTrash') {
          setEditBlockReason('inTrash');
          return;
        }
        if (outcome.status === 'notFound') {
          setEditBlockReason('notFound');
          return;
        }
        if (outcome.status === 'forbidden') {
          return;
        }
        console.error(outcome.error);
        setEditBlockReason('error');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEdit(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, navigate, t, applyLoadedProduct]);

  const handleRestoreFromTrash = useCallback(async () => {
    if (!id || isRestoringFromTrash) return;
    setIsRestoringFromTrash(true);
    try {
      await restoreDeletedProduct(id);
      notifications.show({
        title: t('dashboard.notifications.success'),
        message: t('editProduct.inTrashRestoreSuccess'),
        color: 'green',
      });
      setIsLoadingEdit(true);
      setEditBlockReason(null);
      const outcome = await loadProductForEdit(id, navigate, t);
      if (outcome.status === 'ok') {
        setCanEditProduct(true);
        applyLoadedProduct(outcome.product, id);
      } else if (outcome.status === 'inTrash') {
        setEditBlockReason('inTrash');
      } else if (outcome.status === 'notFound') {
        setEditBlockReason('notFound');
      } else if (outcome.status === 'error') {
        console.error(outcome.error);
        setEditBlockReason('error');
      }
    } catch (error) {
      console.error(error);
      const msg =
        isApiError(error) && error.response?.data && typeof error.response.data === 'object'
          ? (error.response.data as { message?: string }).message
          : undefined;
      notifications.show({
        title: t('dashboard.notifications.error'),
        message: msg || t('editProduct.inTrashRestoreError'),
        color: 'red',
      });
    } finally {
      setIsLoadingEdit(false);
      setIsRestoringFromTrash(false);
    }
  }, [id, isRestoringFromTrash, navigate, t, applyLoadedProduct]);

  const handleUpdateSuccess = useCallback(
    (saved: IProduct, submitted: ProductSubmissionData) => {
      const metadata =
        cloneMetadata((saved.metadata ?? submitted.metadata) as IProduct['metadata']) ?? undefined;
      const savedForm: ProductFormData = {
        productName: saved.productName ?? submitted.productName,
        productPage: saved.productPage ?? submitted.productPage,
        articleNumber: saved.articleNumber ?? submitted.articleNumber,
        isPublic: saved.isPublic ?? submitted.isPublic,
        descriptionHtml: saved.descriptionHtml ?? submitted.descriptionHtml ?? '',
        metadata,
        category: saved.category ?? submitted.category ?? '',
        linkShareMode:
          (saved.linkShareMode as LinkShareMode | undefined) ?? submitted.linkShareMode ?? 'open',
        linkSharePassword: '',
        updateLinkSharePassword: false,
      };
      const baselineForm: ProductFormData = {
        ...savedForm,
        metadata: cloneMetadata(metadata),
      };

      setProductData((prev) => ({
        ...prev,
        ...savedForm,
        linkSharePasswordSet: Boolean(saved.linkSharePasswordSet),
      }));
      setLiveFormValues(savedForm);
      setModelFile(null);
      setModelReplacement(false);
      setEditBaseline({
        form: baselineForm,
        linkSharePasswordSet: Boolean(saved.linkSharePasswordSet),
        previewUri: modelPreviewUri,
        cameraOrbit: initialCameraOrbit,
        cameraTarget: initialCameraTarget,
        cameraFov: initialCameraFieldOfView,
        annotations,
        presentation: presentationSettings,
        hasNewModelFile: false,
      });
    },
    [
      modelPreviewUri,
      initialCameraOrbit,
      initialCameraTarget,
      initialCameraFieldOfView,
      annotations,
      presentationSettings,
      setModelFile,
    ]
  );

  const { submitProduct, isSubmitting, successPopupData, setSuccessPopupData } =
    useProductSubmission({
      modelFile,
      modelPreviewUri,
      initialCameraOrbit,
      initialCameraTarget,
      initialCameraFieldOfView,
      nonGltfModel,
      preparedGlb,
      id: id || undefined,
      annotations,
      loadedModelViewerSettingsRef,
      presentationSettings,
      modelReplacement,
      onUpdateSuccess: handleUpdateSuccess,
    });

  const productFormInitialValues = useMemo(() => {
    const { linkSharePasswordSet: _omit, ...rest } = productData;
    return rest;
  }, [
    productData.productName,
    productData.productPage,
    productData.articleNumber,
    productData.isPublic,
    productData.descriptionHtml,
    productData.category,
    productData.linkShareMode,
    productData.linkSharePassword,
    productData.updateLinkSharePassword,
    productData.metadata,
  ]);

  const currentEditSnapshot = useMemo((): ProductEditDirtySnapshot | null => {
    if (!liveFormValues) return null;
    return {
      form: liveFormValues,
      linkSharePasswordSet: Boolean(productData.linkSharePasswordSet),
      previewUri: modelPreviewUri,
      cameraOrbit: initialCameraOrbit,
      cameraTarget: initialCameraTarget,
      cameraFov: initialCameraFieldOfView,
      annotations,
      presentation: presentationSettings,
      hasNewModelFile: Boolean(modelFile),
    };
  }, [
    liveFormValues,
    productData.linkSharePasswordSet,
    modelPreviewUri,
    initialCameraOrbit,
    initialCameraTarget,
    initialCameraFieldOfView,
    annotations,
    presentationSettings,
    modelFile,
  ]);

  const hasPendingChanges = useMemo(() => {
    if (!id) return Boolean(modelUrl);
    return currentEditSnapshot
      ? hasPendingProductEditChanges(editBaseline, currentEditSnapshot)
      : false;
  }, [id, modelUrl, editBaseline, currentEditSnapshot]);

  const handleFormSubmit = useCallback(
    (data: ProductFormData) => {
      submitProduct({
        ...data,
        linkSharePasswordSet: productData.linkSharePasswordSet,
      });
    },
    [submitProduct, productData.linkSharePasswordSet]
  );

  const exportNonGltfModel = useCallback(
    () =>
      exportModelAsGlb({
        modelFile,
        modelUrl,
        productId: id || undefined,
        nonGltfModel,
        preparedGlb,
        downloadGlb,
        downloadFile,
        onError: (opts) => notifications.show({ ...opts, color: 'red' }),
        getGlbFileName: (file) =>
          file.name.replace(LoadInformation.getFileExtension(file.name), '.glb'),
      }),
    [modelFile, modelUrl, id, nonGltfModel, preparedGlb]
  );

  const cancelModelReplacement = useCallback(() => {
    setShowModelUploader(false);
    setModelFile(null);
    setModelReplacement(false);
    setModelUrl(id ? productGlbUrl(id) : '');
  }, [id, setModelFile, setModelUrl]);

  const handleResetForm = useCallback(() => {
    setModelFile(null);
    setModelUrl('');
    setModelPreviewUri('');
    setInitialCameraOrbit('');
    setInitialCameraTarget('');
    setInitialCameraFieldOfView('');
    setNonGltfModel(null);
    setShowModelUploader(false);
    setModelReplacement(false);
    setAnnotations([]);
    setProductData(EMPTY_FORM);
    setLiveFormValues(null);
    setEditBaseline(null);
    window.history.replaceState({}, '', clientRoutes.createproduct);
    window.location.reload();
  }, [setModelFile, setModelUrl, setNonGltfModel]);

  const showWorkspace = !session.loading && (!id || canEditProduct);
  const isEditingExisting = Boolean(id);

  return {
    t,
    session,
    color,
    id,
    isEditingExisting,
    canEditProduct,
    editBlockReason,
    isLoadingEdit,
    isRestoringFromTrash,
    handleRestoreFromTrash,
    showWorkspace,
    modelViewerRef,
    annotationEditorRef,
    modelFile,
    modelUrl,
    nonGltfModel,
    preparedGlb,
    setModelFile,
    applyPreparedModel,
    liveFormValues,
    modelPreviewUri,
    setModelPreviewUri,
    initialCameraOrbit,
    setInitialCameraOrbit,
    initialCameraTarget,
    setInitialCameraTarget,
    initialCameraFieldOfView,
    setInitialCameraFieldOfView,
    showModelUploader,
    setShowModelUploader,
    modelReplacement,
    setModelReplacement,
    annotations,
    setAnnotations,
    isAnnotationMode,
    setIsAnnotationMode,
    presentationSettings,
    setPresentationSettings,
    availableAnimations,
    productData,
    productFormInitialValues,
    suggestedTags,
    previousTags,
    modelUploadProductName,
    isSubmitting,
    hasPendingChanges,
    successPopupData,
    setSuccessPopupData,
    handleFormSubmit,
    handleFormValuesChange,
    exportNonGltfModel,
    cancelModelReplacement,
    handleResetForm,
  };
}
